/**
 * QuantumRecorder — opt-in recording and replay of quantum operations.
 *
 * Attach to a QuantumPropertyManager via `manager.setRecorder(recorder)`.
 * When recording is active, lifecycle hooks log every state-mutating
 * operation. The log can be replayed via replayLog() to recreate
 * identical quantum state — measurements are forced to their recorded
 * outcomes using forced_measure_properties.
 *
 * For gate recording, call wrapGate() around each WASM gate call.
 */

import { getModule } from "./QuantumForgeLoader";
import type { QuantumPropertyManager, QuantumRecorderHook } from "./QuantumPropertyManager";
import type { QuantumOperation, SerializedPredicate } from "./QuantumOperationLog";
import type { QuantumProperty as QFProperty, Predicate as QFPredicate } from "./quantum-forge-api.mjs";

export interface PredicateSpec {
  property: QFProperty;
  value: number;
  isEqual: boolean;
}

export class QuantumRecorder implements QuantumRecorderHook {
  private _recording = false;
  private _log: QuantumOperation[] = [];
  private _handleToIndex: Map<QFProperty, number> = new Map();
  private _nextIndex = 0;
  private readonly _manager: QuantumPropertyManager;

  constructor(manager: QuantumPropertyManager) {
    this._manager = manager;
  }

  // -- QuantumRecorderHook implementation --

  onAcquire(prop: QFProperty): void {
    if (!this._recording) return;
    const index = this._nextIndex++;
    this._handleToIndex.set(prop, index);
    this._log.push({ op: "acquire", index });
  }

  onRelease(prop: QFProperty, value: number): void {
    if (!this._recording) return;
    const index = this._handleToIndex.get(prop);
    if (index !== undefined) {
      this._log.push({ op: "release", index, value });
    }
  }

  onSetProperty(id: string, prop: QFProperty): void {
    if (!this._recording) return;
    const index = this._handleToIndex.get(prop);
    if (index !== undefined) {
      this._log.push({ op: "assign", index, id });
    }
  }

  onDeleteProperty(id: string): void {
    if (!this._recording) return;
    this._log.push({ op: "unassign", id });
  }

  // -- Gate recording --

  /**
   * Build WASM predicate objects from PredicateSpec array.
   */
  buildWasmPredicates(specs: PredicateSpec[]): QFPredicate[] {
    return specs.map((s) =>
      s.isEqual ? s.property.is(s.value) : s.property.is_not(s.value),
    );
  }

  /**
   * Serialize predicates for the operation log.
   */
  serializePredicates(specs: PredicateSpec[]): SerializedPredicate[] | undefined {
    if (specs.length === 0) return undefined;
    return specs.map((s) => {
      const index = this._handleToIndex.get(s.property);
      return {
        propertyIndex: index ?? -1,
        value: s.value,
        isEqual: s.isEqual,
      };
    });
  }

  /**
   * Record a gate operation. Call this when recording is active
   * and you want to log a gate call for replay.
   */
  recordOp(op: QuantumOperation): void {
    if (!this._recording) return;
    this._log.push(op);
  }

  /**
   * Get the recorded index for a property handle.
   */
  getIndex(prop: QFProperty): number | undefined {
    return this._handleToIndex.get(prop);
  }

  // -- Recording API --

  /** Begin recording quantum operations. Resets any existing log. */
  startRecording(): void {
    this._recording = true;
    this._log = [];
    this._handleToIndex.clear();
    this._nextIndex = 0;

    // Assign indices to all currently-live handles so operations
    // on pre-existing properties are tracked correctly.
    for (const prop of this._manager._getProperties().values()) {
      const index = this._nextIndex++;
      this._handleToIndex.set(prop, index);
    }
    for (const prop of this._manager._getPool()) {
      const index = this._nextIndex++;
      this._handleToIndex.set(prop, index);
    }
  }

  /** Stop recording and return the captured log. */
  stopRecording(): QuantumOperation[] {
    this._recording = false;
    return [...this._log];
  }

  /** Whether recording is currently active. */
  isRecording(): boolean {
    return this._recording;
  }

  /** Get a copy of the current operation log (even while recording). */
  getOperationLog(): QuantumOperation[] {
    return [...this._log];
  }

  /**
   * Replay an operation log to recreate quantum state from scratch.
   * Clears all existing state on the manager first. Measurements are
   * forced to their recorded outcomes via forced_measure_properties.
   */
  replayLog(operations: QuantumOperation[]): void {
    // Clear manager state
    this._manager.clear();
    this._recording = false;
    this._log = [];
    this._handleToIndex.clear();
    this._nextIndex = 0;

    const module = getModule();
    const dimension = this._manager.dimension;
    const indexToHandle = new Map<number, QFProperty>();
    const replayPool: QFProperty[] = [];

    for (const entry of operations) {
      switch (entry.op) {
        case "acquire": {
          let prop: QFProperty;
          if (replayPool.length > 0) {
            prop = replayPool.pop()!;
          } else {
            prop = module.QuantumForge.createQuantumProperty(dimension);
          }
          indexToHandle.set(entry.index, prop);
          break;
        }

        case "release": {
          const prop = indexToHandle.get(entry.index);
          if (prop) {
            module.reset(prop, entry.value);
            replayPool.push(prop);
          }
          break;
        }

        case "assign": {
          const prop = indexToHandle.get(entry.index);
          if (prop) {
            this._manager._getProperties().set(entry.id, prop);
          }
          break;
        }

        case "unassign": {
          this._manager._getProperties().delete(entry.id);
          break;
        }

        case "cycle": {
          const prop = indexToHandle.get(entry.index);
          if (prop) {
            const preds = this._replayPredicates(entry.predicates, indexToHandle);
            if (entry.fraction === 1 && !preds) {
              module.cycle(prop);
            } else {
              module.cycle(prop, entry.fraction, preds);
            }
          }
          break;
        }

        case "shift": {
          const prop = indexToHandle.get(entry.index);
          if (prop) {
            const preds = this._replayPredicates(entry.predicates, indexToHandle);
            if (entry.fraction === 1 && !preds) {
              module.shift(prop);
            } else {
              module.shift(prop, entry.fraction, preds);
            }
          }
          break;
        }

        case "i_swap": {
          const prop1 = indexToHandle.get(entry.index1);
          const prop2 = indexToHandle.get(entry.index2);
          if (prop1 && prop2) {
            const preds = this._replayPredicates(entry.predicates, indexToHandle);
            module.i_swap(prop1, prop2, entry.fraction, preds);
          }
          break;
        }

        case "clock": {
          const prop = indexToHandle.get(entry.index);
          if (prop) {
            const preds = this._replayPredicates(entry.predicates, indexToHandle);
            module.clock(prop, entry.fraction, preds);
          }
          break;
        }

        case "y": {
          const prop = indexToHandle.get(entry.index);
          if (prop) {
            const preds = this._replayPredicates(entry.predicates, indexToHandle);
            if (entry.fraction === 1 && !preds) {
              module.y(prop);
            } else {
              module.y(prop, entry.fraction, preds);
            }
          }
          break;
        }

        case "hadamard": {
          const prop = indexToHandle.get(entry.index);
          if (prop) {
            const preds = this._replayPredicates(entry.predicates, indexToHandle);
            if (entry.fraction === 1 && !preds) {
              module.hadamard(prop);
            } else {
              module.hadamard(prop, entry.fraction, preds);
            }
          }
          break;
        }

        case "inverse_hadamard": {
          const prop = indexToHandle.get(entry.index);
          if (prop) {
            const preds = this._replayPredicates(entry.predicates, indexToHandle);
            module.inverse_hadamard(prop, preds);
          }
          break;
        }

        case "swap": {
          const prop1 = indexToHandle.get(entry.index1);
          const prop2 = indexToHandle.get(entry.index2);
          if (prop1 && prop2) {
            const preds = this._replayPredicates(entry.predicates, indexToHandle);
            module.swap(prop1, prop2, preds);
          }
          break;
        }

        case "phase_rotate": {
          const preds = this._replayPredicates(entry.predicates, indexToHandle);
          if (preds) {
            module.phase_rotate(preds, entry.angle);
          }
          break;
        }

        case "measure_predicate": {
          // During replay, we don't force measure_predicate outcomes —
          // the state should be deterministic from prior forced measurements.
          const preds = this._replayPredicates(entry.predicates, indexToHandle);
          if (preds) {
            module.measure_predicate(preds);
          }
          break;
        }

        case "reset": {
          const prop = indexToHandle.get(entry.index);
          if (prop) {
            module.reset(prop, entry.value);
          }
          break;
        }

        case "measure": {
          const props = entry.indices.map((i) => indexToHandle.get(i)).filter(Boolean) as QFProperty[];
          if (props.length === entry.indices.length) {
            module.forced_measure_properties(props, entry.outcomes);
          }
          break;
        }
      }
    }

    // Restore internal pool from replay pool
    this._manager._setPool(replayPool);

    // Rebuild _handleToIndex from indexToHandle for future recording
    this._handleToIndex.clear();
    for (const [index, handle] of indexToHandle) {
      this._handleToIndex.set(handle, index);
    }
    this._nextIndex = operations.reduce((max, op) => {
      if ("index" in op && typeof op.index === "number") return Math.max(max, op.index + 1);
      if ("index1" in op) {
        const dualOp = op as { index1: number; index2: number };
        return Math.max(max, dualOp.index1 + 1, dualOp.index2 + 1);
      }
      if ("indices" in op) {
        const measureOp = op as { indices: number[] };
        const maxIdx = Math.max(...measureOp.indices);
        return Math.max(max, maxIdx + 1);
      }
      return max;
    }, 0);
  }

  // -- Private helpers --

  private _replayPredicates(
    serialized: SerializedPredicate[] | undefined,
    indexToHandle: Map<number, QFProperty>,
  ): QFPredicate[] | undefined {
    if (!serialized || serialized.length === 0) return undefined;
    const preds: QFPredicate[] = [];
    for (const sp of serialized) {
      const prop = indexToHandle.get(sp.propertyIndex);
      if (!prop) return undefined;
      preds.push(sp.isEqual ? prop.is(sp.value) : prop.is_not(sp.value));
    }
    return preds;
  }
}
